/* eslint-disable @nx/enforce-module-boundaries */
import { Box, Text, VStack } from '@chakra-ui/react';

import { AccordionPanel } from '@gamma/ui/layout';
import { i18n } from '../../../../../../../../../../../../shared/localization';
import {
  DetailsGrid,
  DetailsGridRow,
} from '../../../../../../../../../../components';
import { Alert, AnomalyAlert } from '@investigator/shared/generated/graphql';

interface AlertRowSplitBaselineProps {
  item: Alert | undefined;
}

// Type guard to check if alert is an AnomalyAlert
function isAnomalyAlert(alert?: Alert): alert is AnomalyAlert {
  // Check alert_type in AlertInfo for definitive identification
  return alert?.alert_info?.alert_type === 'anomaly';
}

function getBehaviorTypeLabel(alert?: Alert): string {
  const name = alert?.alert_info?.alert_name?.toLowerCase() ?? '';

  if (name.includes('ssh')) return 'SSH Clients';
  if (name.includes('user agent')) return 'User Agents';
  if (name.includes('service')) return 'Network Services';

  return '';
}

function buildAllAnomalyMappings(data: Alert | undefined) {
  if (!data || !isAnomalyAlert(data)) return [];
  const result: { entity: string; items: string }[] = [];

  for (let i = 1; i <= 10; i++) {
    const entities = data[`nn${i}_entities` as keyof Alert] as
      | string[]
      | undefined;
    const trainItems = data[`nn${i}_train_items` as keyof Alert] as
      | string[]
      | undefined;

    if (Array.isArray(entities) && Array.isArray(trainItems)) {
      const itemsJoined = trainItems.join(', ');
      for (const entity of entities) {
        result.push({ entity, items: itemsJoined });
      }
    }
  }

  return result;
}

const { baseline } = i18n.pages.detections;

export const AlertRowSplitBaseline = ({ item }: AlertRowSplitBaselineProps) => {
  const type = getBehaviorTypeLabel(item);

  return (
    <VStack mb={2} w="100%" alignItems="start">
      <Box w="100%">
        <AccordionPanel title={baseline.howDetectionWorks.title} reduceMotion>
          {baseline.howDetectionWorks.body}
        </AccordionPanel>
        <AccordionPanel title={baseline.similarSubnets.title} reduceMotion>
          <VStack alignItems="start">
            <Text>
              {i18n.formatString(
                baseline.similarSubnets.description,
                isAnomalyAlert(item) ? (item.source_ip ?? '') : '',
              )}
            </Text>
            <DetailsGrid>
              <DetailsGridRow title={<Text fontWeight={600}>Subnets</Text>}>
                <Text fontWeight={600}>
                  {i18n.formatString(baseline.similarSubnets.columnLabel, type)}
                </Text>
              </DetailsGridRow>
              {buildAllAnomalyMappings(item).map((mapping) => (
                <DetailsGridRow title={mapping.entity}>
                  <Text>{mapping.items}</Text>
                </DetailsGridRow>
              ))}
            </DetailsGrid>
          </VStack>
        </AccordionPanel>
        <AccordionPanel
          title={
            <Box textAlign="left">
              {i18n.formatString(
                baseline.differentEntities.title,
                isAnomalyAlert(item) ? (item.item ?? '') : '',
              )}
            </Box>
          }
          reduceMotion
        >
          <VStack alignItems="start">
            <Text color="text.secondary">
              {i18n.formatString(
                baseline.differentEntities.description,
                isAnomalyAlert(item) ? (item.source_ip ?? '') : '',
              )}
            </Text>
            <DetailsGrid>
              {(isAnomalyAlert(item)
                ? (item.item_assoc_entities ?? [])
                : []
              ).map((value) => (
                <DetailsGridRow key={value} title={value}>
                  <span />
                </DetailsGridRow>
              ))}
            </DetailsGrid>
          </VStack>
        </AccordionPanel>
      </Box>
    </VStack>
  );
};
